import { Link } from "@tanstack/react-router";
import type { ActionRecord, AssuranceVideoWithFlags, FlagEdit, FlagWithEdits, SafetyVideoStatus } from "@unbox/api";
import {
  AlertTriangle,
  ArrowNarrowRight,
  Calendar,
  CheckVerified02,
  ChevronDown,
  ChevronUp,
  EyeOff,
  FilePlus02,
  FileX03,
  Lock01,
  Plus,
  Share03,
  Shield02,
  ShieldTick,
  ShieldZap,
  VideoRecorderOff,
} from "@untitledui/icons";
import { useState } from "react";
import { useUser } from "@/api/user";
import { ActionRecordDialog } from "@/components/assurance/action-record-dialog";
import { AddRiskVideoModal } from "@/components/assurance/add-risk-video-modal";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { RiskAiDisclaimer } from "@/components/risk-ai-disclaimer";
import { tierToSeverity } from "@/components/safety-config/risk-groups";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { getStandardRiskDescription, getStandardRiskExplanation } from "@/constants/risk-definitions";
import { useRole } from "@/hooks/use-role";
import { type ActionType, getLogActionLabel } from "@/lib/assurance/action-types";
import { CustomRiskRequest } from "@/pages/safety-beta/components/CustomRiskRequest";
import { RiskCard } from "@/pages/safety-beta/components/RiskCard";
import { RiskSeverityPill } from "@/pages/safety-beta/components/RiskSeverityPill";
import { getRiskTypeIcon } from "@/pages/safety-beta/utils";
import { useMerchant, useWorkspaceId } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";
import { formatDate } from "@/utils/date-conversion";
import { formatEvidence, resolveCustomRiskAttribution } from "@/utils/formatters";
import { pluralize } from "@/utils/text";

// Helper function to combine and sort flag edits and action records
const getVideoHistory = (
  flags: FlagWithEdits[],
  actions: ActionRecord[],
  messages: {
    userId: string;
    message: string;
    createdAt: string;
    id: string;
  }[],
): Array<
  | { type: "flagEdit"; flag: FlagWithEdits; edit: FlagEdit }
  | { type: "actionRecord"; action: ActionRecord }
  | { type: "message"; message: { userId: string; message: string; createdAt: string; id: string } }
> => {
  const flagEdits = flags.flatMap((flag) =>
    flag.edits.map((edit) => ({
      type: "flagEdit" as const,
      flag,
      edit,
      createdAt: new Date(edit.createdAt),
    })),
  );

  const actionRecords = actions.map((action) => ({
    type: "actionRecord" as const,
    action,
    createdAt: new Date(action.createdAt),
  }));

  const messageRecords = messages.map((message) => ({
    type: "message" as const,
    message,
    createdAt: new Date(message.createdAt),
  }));

  return [...flagEdits, ...actionRecords, ...messageRecords]
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    .map((item) => {
      if (item.type === "flagEdit") {
        return { type: "flagEdit", flag: item.flag, edit: item.edit };
      }
      if (item.type === "message") {
        return { type: "message", message: item.message };
      }
      return { type: "actionRecord", action: item.action };
    });
};

const FlagEvidence = ({ flag }: { flag: FlagWithEdits }) => {
  const evidenceType = flag.riskType;
  const hasEvidence = flag.visualEvidence || flag.transcriptEvidence;

  return (
    <div className="self-stretch flex items-start gap-1.5" data-testid={`${evidenceType}-evidence`}>
      {hasEvidence ? (
        <div className="text-sm text-quaternary space-y-1.5">
          {flag.visualEvidence && (
            <p className="space-y-0.5">
              {formatEvidence(flag.visualEvidence, flag.visualEvidenceEn).map((ev, i) => (
                <span key={`${ev.timestamp}-${i}`} className="block">
                  {ev.timestamp && (
                    <span className="underline cursor-pointer hover:text-brand-600">[{ev.timestamp}]</span>
                  )}
                  {ev.text}
                  {ev.translation && <em> ({ev.translation})</em>}
                </span>
              ))}
            </p>
          )}
          {flag.transcriptEvidence && (
            <p className="space-y-0.5">
              {formatEvidence(flag.transcriptEvidence, flag.transcriptEvidenceEn).map((ev, i) => (
                <span key={`${ev.timestamp}-${i}`} className="block">
                  {ev.timestamp && (
                    <span className="underline cursor-pointer hover:text-brand-600">[{ev.timestamp}]</span>
                  )}
                  {ev.text}
                  {ev.translation && <em> ({ev.translation})</em>}
                </span>
              ))}
            </p>
          )}
        </div>
      ) : (
        <p className="text-quaternary text-sm font-normal leading-tight">
          {getStandardRiskExplanation(flag.riskTypeShort) || flag.explanation}
        </p>
      )}
    </div>
  );
};

const getFlagEvidence = (flag: FlagWithEdits) => {
  const evidence = [
    ...formatEvidence(flag.transcriptEvidence, flag.transcriptEvidenceEn),
    ...formatEvidence(flag.visualEvidence, flag.visualEvidenceEn),
  ];
  if (evidence.length === 0) {
    evidence.push({ timestamp: "", text: getStandardRiskExplanation(flag.riskTypeShort) ?? flag.explanation ?? "" });
  }
  return evidence;
};

const RiskSummaryCard = ({ flag }: { flag: FlagWithEdits }) => {
  const severity = tierToSeverity(flag.riskTier ?? 4);

  return (
    <div className="self-stretch p-4 bg-white rounded-md flex flex-col justify-start items-start gap-2">
      <div className="flex justify-start items-center gap-2">
        {getRiskTypeIcon(flag.riskType, "w-4 h-4 text-error-primary shrink-0")}
        <span className="justify-start text-error-primary text-sm font-medium leading-tight">
          {flag.edits.find((e) => e.actionTaken === "edit_flag")?.newFlag ?? flag.riskTypeShort}
        </span>
        <RiskSeverityPill name={severity} severity={severity} />
      </div>
      <FlagEvidence flag={flag} />
    </div>
  );
};

// Safety Risks Section Component
// The states where the pipeline produced no assurance result. Each names why, so the
// panel can say what to do about it rather than reporting a generic failure.
const RISKS_UNKNOWN_PANELS: Partial<
  Record<SafetyVideoStatus, { icon: typeof FileX03; background: string; title: string; description: string }>
> = {
  unableToAnalyse: {
    icon: FileX03,
    background: "bg-utility-error-50",
    title: "Unable to Analyse",
    description:
      "An error occurred while trying to analyse this video. If you still need this video analysed, please contact support",
  },
  unableToAccess: {
    icon: VideoRecorderOff,
    background: "bg-utility-warning-50",
    title: "Unable to Access",
    description: "Our system was unable to access this video",
  },
  videoPrivated: {
    icon: Lock01,
    background: "bg-utility-blue-50",
    title: "Video Private",
    description:
      "This video has been set to private or age-restricted by the creator, restricting our access. We're unable to analyse content we don't have permission to view.",
  },
  videoRemoved: {
    icon: VideoRecorderOff,
    background: "bg-utility-gray-blue-50",
    title: "Video removed",
    description: "This video has been removed and is no longer available",
  },
  historic: {
    icon: EyeOff,
    background: "bg-utility-gray-50",
    title: "Video not Analysed",
    description:
      "This video was published before joining Unbox. Safety screening only runs on videos published after your account was set up.",
  },
};

export const SafetyRisksSection = ({ video }: { video: AssuranceVideoWithFlags }) => {
  const risksUnknownPanel = RISKS_UNKNOWN_PANELS[video.status];
  const { role } = useRole();
  const [addRiskOpen, setAddRiskOpen] = useState(false);
  const [confirmedRisksOpen, setConfirmedRisksOpen] = useState(true);
  const [customRiskOpen, setCustomRiskOpen] = useState(false);
  const isReadOnly = role === "agency_user" || role === "restricted_user";
  const merchantId = useMerchant();
  const workspaceId = useWorkspaceId();
  const base = workspaceId ? "/workspace/$workspaceId/merchant/$merchantId" : "/merchant/$merchantId";
  // Built as an object and spread so TanStack accepts the dynamic `to` string (same pattern as
  // CollaborationLink, for the same reason — see its comment for the workspace-scope rationale).
  const reviewRiskLinkProps = {
    to: `${base}/safety/flagged`,
    params: workspaceId ? { workspaceId, merchantId } : { merchantId },
    search: { tab: "needs-review" as const, video_id: video.videoId },
  };

  const activeFlags = video.flags.filter(
    (f) => !f.edits.length || f.edits.slice(-1)[0]?.actionTaken !== "flag_dismissed",
  );
  const unreviewedFlags = activeFlags.filter((f) => !f.edits.length);
  const confirmedFlags = activeFlags.filter((f) => f.edits.length > 0);
  const confirmedCustomFlags = confirmedFlags.filter((f) => resolveCustomRiskAttribution(f).isCustom);
  const confirmedDetectedFlags = confirmedFlags.filter((f) => !resolveCustomRiskAttribution(f).isCustom);
  const existingCustomRisks = confirmedCustomFlags.map((f) => {
    const flag = f as unknown as FlagWithEdits;
    const attribution = resolveCustomRiskAttribution(flag);
    return {
      id: flag.id,
      title: flag.edits.find((e) => e.actionTaken === "edit_flag")?.newFlag ?? flag.riskTypeShort,
      description: getFlagEvidence(flag)[0]?.text ?? "",
      severity: tierToSeverity(flag.riskTier ?? 4),
      flaggedByName: attribution.flaggedByName,
      flaggedAt: attribution.flaggedAt,
    };
  });
  const existingRiskIds = activeFlags.map((f) => f.riskId).filter((riskId): riskId is string => !!riskId);

  return (
    <div className="w-full max-w-[768px] rounded-lg inline-flex flex-col justify-start items-start gap-4">
      <div className="self-stretch inline-flex justify-between items-center">
        <div className="flex justify-start items-center gap-1.5">
          <ShieldTick className="w-5 h-5 text-tertiary" strokeWidth={2} />
          <div className="justify-start text-tertiary text-sm font-medium leading-tight">Identified Risks</div>
        </div>
        <AddRiskVideoModal
          videoId={video.videoId}
          existingRiskIds={existingRiskIds}
          open={addRiskOpen}
          onOpenChange={setAddRiskOpen}
          onSuccess={() => setAddRiskOpen(false)}
        >
          <Button
            color="tertiary"
            size="sm"
            disabled={isReadOnly}
            iconLeading={Plus}
            className="text-brand-600 font-semibold"
          >
            Add Risk
          </Button>
        </AddRiskVideoModal>
      </div>

      <RiskAiDisclaimer className="-mt-2" />

      {risksUnknownPanel && (
        <div
          className={cx(
            "px-4 py-16 rounded-md flex flex-col justify-center items-center gap-5 self-stretch",
            risksUnknownPanel.background,
          )}
        >
          <div className="flex-shrink-0">
            <risksUnknownPanel.icon className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-1 max-w-64 text-center">
            <div className="self-stretch justify-start text-primary text-sm font-medium leading-tight">
              {risksUnknownPanel.title}
            </div>
            <div className="self-stretch justify-start text-tertiary text-xs font-normal leading-[18px]">
              {risksUnknownPanel.description}
            </div>
          </div>
          {video.videoUrl && (
            <a href={video.videoUrl} className="hover:underline self-stretch justify-start text-primary text-sm mt-2">
              <div className="flex items-center justify-center gap-2 font-semibold">
                View on Tik Tok <Share03 className="h-4 w-4" />
              </div>
            </a>
          )}
        </div>
      )}

      {(video.status === "noRisksFound" || video.status === "noActionRequired") && activeFlags.length === 0 && (
        <div className="self-stretch px-4 py-6 bg-success-primary rounded-md inline-flex items-center gap-3">
          <div className="flex-shrink-0">
            <CheckVerified02 className="w-8 h-8 text-success-primary" strokeWidth={1.5} />
          </div>
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
            <div className="self-stretch justify-start text-success-primary text-sm font-medium leading-tight">
              Video reviewed and no risks found!
            </div>
            <div className="self-stretch justify-start text-success-secondary text-sm font-normal leading-tight">
              Want to flag a risk on this video? Use the "Add Risk" button above.
            </div>
          </div>
        </div>
      )}

      {unreviewedFlags.length > 0 && (
        <div className="self-stretch p-4 bg-error-secondary rounded-md flex flex-col justify-start items-start gap-3">
          <div className="self-stretch inline-flex justify-between items-center">
            <div className="flex justify-start items-center gap-1.5">
              <Shield02 className="w-5 h-5 text-tertiary" strokeWidth={2} />
              <div className="justify-start text-tertiary text-sm font-semibold leading-tight">
                {pluralize(unreviewedFlags.length, "risk identified", "risks identified")}
              </div>
            </div>
            <Link
              {...reviewRiskLinkProps}
              className="flex items-center gap-1 text-sm font-semibold text-brand-600 hover:underline"
            >
              Review Risk <ArrowNarrowRight className="w-4 h-4" />
            </Link>
          </div>
          {unreviewedFlags.map((flag) => (
            <RiskSummaryCard key={flag.id} flag={flag as unknown as FlagWithEdits} />
          ))}
        </div>
      )}

      {confirmedFlags.length > 0 && (
        <Collapsible open={confirmedRisksOpen} onOpenChange={setConfirmedRisksOpen} className="self-stretch">
          <div className="bg-unbox-cream-50 rounded-md p-3 flex flex-col gap-4">
            <CollapsibleTrigger className="flex w-full items-center gap-1.5">
              <ShieldZap className="w-5 h-5 text-secondary" />
              <span className="text-sm font-medium text-secondary flex-1 text-left">Confirm Identified Risks</span>
              {confirmedRisksOpen ? (
                <ChevronUp className="w-5 h-5 text-secondary" />
              ) : (
                <ChevronDown className="w-5 h-5 text-secondary" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="flex flex-col gap-3">
                {confirmedDetectedFlags.map((f) => {
                  const flag = f as unknown as FlagWithEdits;
                  const attribution = resolveCustomRiskAttribution(flag);
                  return (
                    <RiskCard
                      key={flag.id}
                      readOnly
                      icon={getRiskTypeIcon(flag.riskType)}
                      title={flag.edits.find((e) => e.actionTaken === "edit_flag")?.newFlag ?? flag.riskTypeShort}
                      severity={tierToSeverity(flag.riskTier ?? 4)}
                      tooltipInfo={getStandardRiskDescription(flag.riskId ?? "")}
                      evidence={getFlagEvidence(flag)}
                      typeOfRisk={attribution.isCustom ? "custom" : "default"}
                      flaggedByName={attribution.flaggedByName}
                      flaggedAt={attribution.flaggedAt}
                    />
                  );
                })}
                <CustomRiskRequest
                  videoId={video.videoId}
                  existingCustomRisks={existingCustomRisks}
                  open={customRiskOpen}
                  onOpenChange={setCustomRiskOpen}
                  onRiskRemoved={() => {}}
                  readOnly
                />
                {!customRiskOpen && (
                  <Button
                    color="secondary"
                    size="sm"
                    iconLeading={<Plus className="w-4 h-4 text-quaternary" />}
                    onClick={() => setCustomRiskOpen(true)}
                    className="!bg-unbox-cream-50"
                  >
                    Add Risk
                  </Button>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}
    </div>
  );
};

// Individual Action Item Component
const ActionItem = ({ action }: { action: ActionRecord }) => {
  const { data: user } = useUser(action.userId ?? undefined);

  return (
    <>
      <div className="self-stretch flex flex-col justify-start items-start gap-2">
        <div className="self-stretch inline-flex justify-between items-center">
          <div className="flex justify-start items-center gap-2">
            <Badge className="bg-success-100 text-success-800 hover:bg-success-200">
              {getLogActionLabel(action.actionType as ActionType)}
            </Badge>
            <span className="text-xs text-quaternary">by {action.userId ? user?.email : "system"}</span>
          </div>
          <div className="justify-start text-tertiary text-xs font-normal leading-[18px]">
            {formatDate(action.createdAt)}
          </div>
        </div>
        {action.notes.length > 0 && (
          <div className="self-stretch justify-start text-quaternary text-sm font-normal leading-tight">
            {action.notes}
          </div>
        )}
      </div>
      <div className="self-stretch h-0 border-t border-border-primary" />
    </>
  );
};

// Actions Section Component
export const ActionsSection = ({ video }: { video: AssuranceVideoWithFlags }) => {
  const [openDialog, setOpenDialog] = useState<"add-action">();

  return (
    <div className="w-full max-w-[768px] rounded-lg inline-flex flex-col justify-start items-start gap-4">
      <div className="self-stretch inline-flex justify-between items-center">
        <div className="flex justify-start items-center gap-1.5">
          <FilePlus02 className="w-5 h-5 text-tertiary" strokeWidth={2} />
          <div className="justify-start text-tertiary text-sm font-medium leading-tight">Actions</div>
        </div>
        <Dialog
          open={openDialog === "add-action"}
          onOpenChange={(open) => setOpenDialog(open ? "add-action" : undefined)}
        >
          <DialogTrigger asChild>
            <Button color="tertiary" size="sm" iconLeading={Plus} className="text-brand-600 font-semibold">
              Log Action
            </Button>
          </DialogTrigger>
          {openDialog === "add-action" && (
            <ActionRecordDialog
              videos={[
                { videoId: video.videoId, creatorId: video.creatorId, thumbnail: null, productList: video.productList },
              ]}
              onClose={() => setOpenDialog(undefined)}
              preselectedCategory="log"
            />
          )}
        </Dialog>
      </div>

      {video.actions.length === 0 && (
        <div className="self-stretch px-4 py-6 bg-warning-50 rounded-md inline-flex justify-center items-start gap-1">
          <div className="w-20 self-stretch flex justify-center items-center gap-2">
            <AlertTriangle className="w-8 h-8 text-warning-700" strokeWidth={2} />
          </div>
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
            <div className="self-stretch justify-start text-warning-700 text-sm font-medium leading-tight">
              This video has risks identified
            </div>
            <div className="self-stretch justify-start text-warning-500 text-sm font-normal leading-tight">
              Take action to resolve
            </div>
          </div>
        </div>
      )}

      {video.actions.length > 0 && (
        <div className="self-stretch flex flex-col justify-start items-start gap-5">
          <div className="self-stretch h-0 border-t border-border-primary" />
          {video.actions.map((action) => (
            <ActionItem key={action.id} action={action as unknown as ActionRecord} />
          ))}
        </div>
      )}
    </div>
  );
};

// Individual History Item Components
const FlagEditHistoryItem = ({ flag, edit }: { flag: FlagWithEdits; edit: FlagEdit }) => {
  const { data: user } = useUser(edit.userId ?? undefined);

  const getFlagEditTitle = () => {
    if (edit.actionTaken === "flag_dismissed") {
      return `Flag "${flag.riskTypeShort}" Dismissed`;
    }
    if (edit.actionTaken === "edit_flag") {
      return `Flag edited: "${edit.originalFlag}" → "${edit.newFlag}"`;
    }
    return `Flag "${flag.riskTypeShort}" Confirmed`;
  };

  return (
    <>
      <div className="self-stretch flex flex-col justify-start items-start gap-3">
        <div className="self-stretch inline-flex justify-between items-start">
          <div className="inline-flex flex-col justify-center items-start gap-0.5">
            <div className="justify-start text-tertiary_hover text-sm font-medium leading-tight">
              {getFlagEditTitle()}
            </div>
            <div className="justify-start text-quaternary text-sm font-medium leading-tight">
              By: {edit.userId ? (user?.email ?? "Unknown") : "System"}
            </div>
          </div>
          <div className="justify-start text-tertiary text-xs font-normal leading-[18px]">
            {formatDate(edit.createdAt)}
          </div>
        </div>
        {edit.feedbackReason && (
          <div className="self-stretch justify-start text-quaternary text-sm font-normal leading-tight">
            {edit.feedbackReason}
          </div>
        )}
      </div>
      <div className="self-stretch h-0 border-t border-border-primary" />
    </>
  );
};

// NOTE: This component is intentionally kept for future use to render message history items
// in the safety review timeline once messaging-to-creator events are surfaced here.
const _MessageHistoryItem = ({ message }: { message: { userId: string; message: string; createdAt: string } }) => {
  const { data: user } = useUser(message.userId);

  return (
    <>
      <div className="self-stretch flex flex-col justify-start items-start gap-3">
        <div className="self-stretch inline-flex justify-between items-start">
          <div className="inline-flex flex-col justify-center items-start gap-0.5">
            <div className="justify-start text-tertiary_hover text-sm font-medium leading-tight">
              Message sent to creator
            </div>
            <div className="justify-start text-quaternary text-sm font-medium leading-tight">
              By: {user?.email || "Unknown"}
            </div>
          </div>
          <div className="justify-start text-tertiary text-xs font-normal leading-[18px]">
            {formatDate(message.createdAt)}
          </div>
        </div>
        {message.message && (
          <div className="self-stretch justify-start text-quaternary text-sm font-normal leading-tight">
            {message.message}
          </div>
        )}
      </div>
      <div className="self-stretch h-0 border-t border-border-primary" />
    </>
  );
};

const ActionRecordHistoryItem = ({ action }: { action: ActionRecord }) => {
  const { data: user } = useUser(action.userId ?? undefined);

  return (
    <>
      <div className="self-stretch flex flex-col justify-start items-start gap-3">
        <div className="self-stretch inline-flex justify-between items-start">
          <div className="inline-flex flex-col justify-center items-start gap-0.5">
            <div className="justify-start text-tertiary_hover text-sm font-medium leading-tight">
              Action: {getLogActionLabel(action.actionType as ActionType)}
            </div>
            <div className="justify-start text-quaternary text-sm font-medium leading-tight">
              By: {action.userId ? (user?.email ?? "unknown") : "system"}
            </div>
          </div>
          <div className="justify-start text-tertiary text-xs font-normal leading-[18px]">
            {formatDate(action.createdAt)}
          </div>
        </div>
        {action.notes && (
          <div className="self-stretch justify-start text-quaternary text-sm font-normal leading-tight">
            {action.notes}
          </div>
        )}
        {action.ticketId && (
          <div className="self-stretch inline-flex items-center gap-1.5 text-xs text-quaternary">
            <span className="font-medium">Ticket ID:</span>
            <code className="font-mono bg-secondary px-1.5 py-0.5 rounded">{action.ticketId}</code>
          </div>
        )}
      </div>
      <div className="self-stretch h-0 border-t border-border-primary" />
    </>
  );
};

// History Section Component
export const HistorySection = ({ video }: { video: AssuranceVideoWithFlags }) => {
  const history = getVideoHistory(
    video.flags as unknown as FlagWithEdits[],
    video.actions as unknown as ActionRecord[],
    video.sentMessages,
  );

  return (
    <div className="w-full max-w-[768px] rounded-lg inline-flex flex-col justify-start items-start gap-4">
      <div className="inline-flex justify-start items-center gap-1.5">
        <Calendar className="w-5 h-5 text-tertiary" strokeWidth={2} />
        <div className="justify-start text-tertiary text-sm font-medium leading-tight">History</div>
      </div>
      {history.length === 0 ? (
        <div className="self-stretch px-4 py-6 bg-secondary rounded-md inline-flex justify-center items-center">
          <div className="text-quaternary text-sm">No history yet</div>
        </div>
      ) : (
        <div className="self-stretch flex flex-col justify-start items-start gap-6">
          <div className="self-stretch inline-flex justify-start items-start gap-6">
            <div className="flex-1 inline-flex flex-col justify-start items-start gap-5">
              <div className="self-stretch h-0 border-t border-border-primary" />
              {history.map((item) => {
                if (item.type === "flagEdit") {
                  return <FlagEditHistoryItem key={`flag-edit-${item.edit.id}`} flag={item.flag} edit={item.edit} />;
                }
                if (item.type === "message") {
                  // Message history no longer shown as we log actions
                  // when messages are sent
                  return null;
                  //   return <MessageHistoryItem key={`message-${item.message.id}`} message={item.message} />;
                }
                return <ActionRecordHistoryItem key={`action-${item.action.id}`} action={item.action} />;
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
